import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { DataGrid, Column } from '@/components/DataGrid';
import { useEngagements, useEngagementGroupings, useLookup } from '@/api/hooks';

type Row = Record<string, unknown>;

const columns: Column<Row>[] = [
  { key: 'Source', header: 'Source' },
  { key: 'EngagementID', header: 'Eng ID' },
  { key: 'ServiceCategory', header: 'Service Category' },
  { key: 'TemplateName', header: 'Template Name' },
  { key: 'Year', header: 'Year' },
  { key: 'ClientName', header: 'Client Name' },
  { key: 'ClientManager', header: 'Client Manager' },
  { key: 'BRCIndustry', header: 'BRC Industry' },
  { key: 'EngagementManager', header: 'Engagement Manager' },
  { key: 'PracticeAreaPartner', header: 'Practice Area Partner' },
  { key: 'RequestStatus', header: 'Status' },
];

export function Engagements() {
  const navigate = useNavigate();
  const [grouping, setGrouping] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const q = useEngagements(grouping || undefined, search || undefined, year || undefined);
  const groupings = useEngagementGroupings();
  const years = useLookup('fiscal-years');

  return (
    // Full-height flex column so the grid scrolls inside a bounded area and its
    // horizontal scrollbar stays on screen (matches View / Change Clients).
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
        <div>
          <label className="label">Client starts with</label>
          <select className="input w-36" value={grouping} onChange={(e) => setGrouping(e.target.value)}>
            <option value="">All</option>
            {(groupings.data ?? []).map((g) => (
              <option key={g.ClientGrouping} value={g.ClientGrouping}>{g.ClientGrouping}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[200px] flex-1">
          <label className="label">Search (Client Name)</label>
          <div className="flex gap-2">
            <input className="input" placeholder="Client name…" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)} />
            <button className="btn-secondary" onClick={() => setSearch(searchInput)}>Search</button>
          </div>
        </div>
        <div>
          <label className="label">Year</label>
          <select className="input w-32" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">All</option>
            {(years.data ?? []).map((y, i) => (
              <option key={i} value={String((y as Row).FiscalYear ?? '')}>{String((y as Row).FiscalYear ?? '')}</option>
            ))}
          </select>
        </div>
        {(grouping || search || year) && (
          <button className="btn-secondary" onClick={() => { setGrouping(''); setSearch(''); setSearchInput(''); setYear(''); }}>
            Clear
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1">
        <DataGrid
          columns={columns}
          rows={q.data ?? []}
          loading={q.isFetching}
          empty="No engagements match."
          rowAction={(row) => (
            <button title="View / Edit" className="text-primary hover:opacity-70"
              onClick={() => navigate(`/engagements/${row.EngagementID}`)}>
              <Eye className="h-4 w-4" />
            </button>
          )}
        />
      </div>
      <p className="flex-shrink-0 text-xs text-muted-foreground">
        Showing up to 500 most-recent matches{q.data ? ` (${q.data.length})` : ''}. Narrow with the filters above.
      </p>
    </div>
  );
}
